// Shared pieces of the per-phase reading charts (Voltage, Power, Frequency,
// Power Factor, Current): turning a device's load logs into one series per
// phase, and the chart look they all use. Pure, so it can be tested on its own.

// One colour per phase, the same on every chart (a validated categorical set:
// blue, orange, aqua).
export const PHASE_COLORS = ["#2a78d6", "#eb6834", "#1baf7a"];

const INK_MUTED = "#8a8f96";
const GRID = "#eef0f3";

// "M630005-Phase 2" -> "Phase 2": the chart is already for one device.
export const phaseName = (name) => String(name ?? "").replace(/^.*-(?=Phase \d)/, "");

// A log's reading for `key`, or null when it has none or `valid` rejects it.
// Logs with a `ct` key are disconnect records with no reading, and would plot
// as drops to 0.
const readingOf = (log, key, valid) => {
  if (!log || log.ts === undefined || Object.prototype.hasOwnProperty.call(log, "ct")) return null;
  const value = Number(log[key]);
  if (log[key] === null || log[key] === undefined || !Number.isFinite(value)) return null;
  return valid(value) ? value : null;
};

// A day's logs split by phase: { "0": [...], "1": [...] }, by time.
const byPhase = (logs = []) => {
  const phases = { 0: [] };
  for (const log of logs) {
    if (!log || log.ts === undefined) continue;
    const phase = log.m === undefined || log.m === 0 ? 0 : log.m;
    (phases[phase] = phases[phase] || []).push(log);
  }
  for (const phase of Object.keys(phases)) {
    phases[phase].sort((a, b) => Number(a.ts) - Number(b.ts));
  }
  return phases;
};

// Readings further apart than this are not joined: the device sent nothing
// in between, and a line would make up readings for that time.
export const MAX_GAP_MS = 15 * 60 * 1000;

// [ts, value] points with a [ts, null] break wherever two are more than
// MAX_GAP_MS apart, so the chart leaves a gap there.
const breakGaps = (points) =>
  points.flatMap((point, i) =>
    i > 0 && point[0] - points[i - 1][0] > MAX_GAP_MS ? [[points[i - 1][0] + 1, null], point] : [point]
  );

// The chart's series for reading `key` ("v", "p", "f", "pf", "c", or "t" with
// logs "temperatureLogs").
// One day: one series per phase of [ts, value] points, named "Phase N" (or
// `label` for a single-phase device), broken where readings are more than 15
// minutes apart. Several days: one series of each day's
// average across phases, in day order.
// `logs` names the day's log list; `valid` drops readings a sensor sends as
// errors rather than values.
export const phaseSeries = (days = [], key, label, { logs = "loadLogs", valid = () => true } = {}) => {
  if (days.length === 1) {
    const phases = byPhase(days[0]?.[logs]);
    const keys = Object.keys(phases).filter((phase) => phases[phase].length > 0);
    return keys.map((phase) => ({
      name: keys.length > 1 ? `Phase ${Number(phase) + 1}` : label,
      data: breakGaps(
        phases[phase]
          .map((log) => [Number(log.ts), readingOf(log, key, valid)])
          .filter(([, value]) => value !== null)
      ),
    }));
  }

  const averages = days.map((day) => {
    const phases = byPhase(day?.[logs]);
    const means = Object.values(phases)
      .map((phaseLogs) => phaseLogs.map((log) => readingOf(log, key, valid)).filter((value) => value !== null))
      .filter((values) => values.length > 0)
      .map((values) => values.reduce((sum, value) => sum + value, 0) / values.length);
    if (means.length === 0) return null;
    return Number((means.reduce((sum, mean) => sum + mean, 0) / means.length).toFixed(2));
  });
  return [{ name: label, data: averages }];
};

// A reading's safe band, e.g. { low: 230, high: 250, normalLabel: "Normal",
// lowLabel: "Low voltage", highLabel: "High voltage" }: both bounds count as
// in range.
export const RANGE_COLORS = { normal: "#1baf7a", out: "#e5383b" };

// Ends the name of a phase's red overlay series ("Phase 1 · out of range").
const OVERLAY_SUFFIX = " · out of range";

const isOutOfRange = (value, { low, high }) => value !== null && value !== undefined && (value < low || value > high);

// "Low voltage" for a reading below the band, "High voltage" above it, null
// inside it.
export const rangeStatus = (value, range) => {
  if (!range || !isOutOfRange(value, range)) return null;
  return value < range.low ? range.lowLabel : range.highLabel;
};

// The point where the line from `a` to `b` crosses `bound`, so a red stretch
// starts exactly where the reading leaves the band.
const crossing = ([t1, v1], [t2, v2], bound) => [t1 + ((bound - v1) / (v2 - v1)) * (t2 - t1), bound];

// The red overlay for each phase series: only its out-of-range stretches,
// with gaps (nulls) between them, drawn on top of the phase's own line. One
// day's [ts, value] series get the band crossings added; several days' daily
// averages keep their category slots. Phases that stay in range get no
// overlay. `count` is how many readings (or days) are out of range, of
// `total` with a reading.
export const outOfRangeSeries = (series = [], range) => {
  let count = 0;
  let total = 0;
  const overlays = [];
  for (const line of series) {
    const points = line.data ?? [];
    const pairs = Array.isArray(points[0]);
    let out = 0;
    let data;
    if (pairs) {
      data = [];
      points.forEach((point, i) => {
        // A gap break (null) ends a red stretch, and nothing crosses it.
        const previous = points[i - 1]?.[1] === null ? undefined : points[i - 1];
        const wasOut = previous && isOutOfRange(previous[1], range);
        if (point[1] === null) {
          if (wasOut) data.push(point);
        } else if (isOutOfRange(point[1], range)) {
          out += 1;
          if (previous && !wasOut) data.push(crossing(previous, point, point[1] < range.low ? range.low : range.high));
          data.push(point);
        } else if (wasOut) {
          data.push(crossing(previous, point, previous[1] < range.low ? range.low : range.high), [point[0], null]);
        }
      });
    } else {
      data = points.map((value) => (isOutOfRange(value, range) ? value : null));
      out = data.filter((value) => value !== null).length;
    }
    if (out > 0) overlays.push({ name: `${line.name}${OVERLAY_SUFFIX}`, data });
    count += out;
    total += points.map((point) => (pairs ? point[1] : point)).filter((value) => value !== null && value !== undefined).length;
  }
  return { overlays, count, total };
};

// "8500" -> "8.5k" for axis ticks on big numbers; small ones as `decimals`.
export const compactNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "";
  const number = Number(value);
  if (Math.abs(number) >= 1000) {
    const thousands = number / 1000;
    return `${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}k`;
  }
  return number.toFixed(decimals);
};

// The smallest 1, 2, 2.5 or 5 x 10^n at or above `value`: an axis top that
// clears the data's peak without overshooting it (85 -> 100, not 200).
export const niceCeil = (value) => {
  if (!Number.isFinite(value) || value <= 0) return value;
  const power = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((multiple) => multiple * power >= value);
  return Number((step * power).toPrecision(12));
};

const dayLabel = (date) =>
  new Date(date).toLocaleString("en-IN", { day: "2-digit", month: "short" });

const timeLabel = (ts) =>
  new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

// The ApexCharts options for a reading chart.
//   unit       shown in tooltips ("V"); omitted for unitless readings
//   axisTitle  the y axis title ("Volts (V)")
//   decimals   { axis, tooltip } digits
//   min, max   fixed y bounds, when the reading has natural ones (PF 0-1)
//   fromZero   for readings measured up from zero (power, current,
//              temperature), rather than ones that sit in a narrow band far
//              from it (voltage, frequency)
//   days       the selected days; more than one switches to a day axis
//   range      the reading's safe band (see RANGE_COLORS): shaded, kept in
//              view, and named in tooltips for readings outside it
//   phaseNames, overlayCount
//              with `range`, the phase series that come first and how many
//              red out-of-range overlays follow them (outOfRangeSeries)
export const readingChartOptions = ({
  unit,
  axisTitle,
  decimals = {},
  min,
  max,
  fromZero = false,
  days = [],
  range,
  phaseNames = [],
  overlayCount = 0,
}) => {
  const multiDay = days.length > 1;
  const axisDigits = decimals.axis ?? 0;
  const tooltipDigits = decimals.tooltip ?? 2;
  const withUnit = (value) => {
    if (value === null || value === undefined) return "-";
    const status = rangeStatus(Number(value), range);
    return `${Number(value).toFixed(tooltipDigits)}${unit ? ` ${unit}` : ""}${status ? ` · ${status}` : ""}`;
  };
  const phaseIndexes = phaseNames.map((_, i) => i);
  // Room around the band, so it shows even when every reading sits inside it.
  const rangePad = range ? (range.high - range.low) / 4 : 0;
  const axisText = { colors: INK_MUTED, fontSize: "11px", fontFamily: "manrope" };

  return {
    chart: {
      type: "line",
      fontFamily: "manrope",
      zoom: { type: "x", enabled: true, autoScaleYaxis: min === undefined },
      toolbar: {
        autoSelected: "zoom",
        tools: { download: true, selection: false, zoom: true, zoomin: true, zoomout: true, pan: false, reset: true },
        export: {
          csv: {
            categoryFormatter: (x) =>
              new Date(x)
                .toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
                .replace(",", ""),
          },
        },
      },
      animations: { enabled: false },
      // Left out without a range: an undefined `events` replaces ApexCharts'
      // defaults and it fails reading `events.beforeMount`.
      ...(range
        ? {
            events: {
              // Hiding a phase hides its red overlay with it.
              legendClick: (chart, seriesIndex) => {
                const overlay = `${phaseNames[seriesIndex]}${OVERLAY_SUFFIX}`;
                if (chart?.w?.globals?.seriesNames?.includes(overlay)) chart.toggleSeries(overlay);
              },
            },
          }
        : {}),
    },
    colors: range
      ? [...phaseIndexes.map((i) => PHASE_COLORS[i % PHASE_COLORS.length]), ...Array(overlayCount).fill(RANGE_COLORS.out)]
      : PHASE_COLORS,
    stroke: { width: multiDay ? 3 : 2.5, curve: "straight" },
    markers: {
      // Several days: a red dot on an out-of-range day even when its
      // neighbours are fine and there is no red stretch to draw.
      size: range && multiDay ? [...phaseIndexes.map(() => 4), ...Array(overlayCount).fill(5)] : multiDay ? 4 : 0,
      strokeWidth: 2,
      strokeColors: "#ffffff",
      hover: { size: 5 },
    },
    ...(range
      ? {
          annotations: {
            yaxis: [
              {
                y: range.low,
                y2: range.high,
                fillColor: RANGE_COLORS.normal,
                opacity: 0.08,
                borderColor: "transparent",
              },
              ...[range.low, range.high].map((y) => ({
                y,
                borderColor: RANGE_COLORS.normal,
                strokeDashArray: 4,
                opacity: 0.6,
              })),
            ],
          },
        }
      : {}),
    dataLabels: { enabled: false },
    grid: {
      borderColor: GRID,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      padding: { left: 8, right: 8 },
    },
    legend: {
      // Shown even for one phase, so the line is named next to the band. The
      // red overlays' own entries are hidden by PhaseReadingChart (not with
      // customLegendItems, which turns off legend clicks).
      ...(range ? { showForSingleSeries: true } : {}),
      position: "top",
      horizontalAlign: "left",
      fontFamily: "manrope",
      fontSize: "12px",
      labels: { colors: "#495057" },
      markers: { width: 10, height: 10, radius: 10 },
      itemMargin: { horizontal: 8 },
    },
    yaxis: {
      // With `fromZero`, the axis runs from 0 (or a round number below a
      // negative low) to a round number above the peak. Left to ApexCharts,
      // a lone spike can put the top at double the peak and flatten the line
      // (an 85 spike gave a 0-200 axis).
      min:
        min ??
        (fromZero
          ? (dataMin) => (dataMin >= 0 ? 0 : -niceCeil(-dataMin))
          : range
          ? (dataMin) => Math.floor(Math.min(dataMin, range.low - rangePad) / rangePad) * rangePad
          : undefined),
      max:
        max ??
        (fromZero
          ? (dataMax) => (dataMax <= 0 ? 0 : niceCeil(dataMax))
          : range
          ? (dataMax) => Math.ceil(Math.max(dataMax, range.high + rangePad) / rangePad) * rangePad
          : undefined),
      tickAmount: range ? 6 : 4,
      labels: { style: axisText, formatter: (value) => compactNumber(value, axisDigits) },
      title: { text: axisTitle, style: { color: INK_MUTED, fontSize: "12px", fontWeight: 500, fontFamily: "manrope" } },
    },
    xaxis: multiDay
      ? {
          type: "category",
          categories: days.map((day) => dayLabel(day.date)),
          labels: { style: axisText },
          axisBorder: { color: GRID },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        }
      : {
          type: "datetime",
          tickAmount: 8,
          labels: { style: axisText, datetimeUTC: false, formatter: (value) => timeLabel(value) },
          axisBorder: { color: GRID },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
    tooltip: {
      shared: false,
      x: { formatter: (value, opts) => (multiDay ? opts?.w?.globals?.categoryLabels?.[opts.dataPointIndex] ?? value : timeLabel(value)) },
      y: {
        formatter: withUnit,
        // A red overlay's point is its phase's reading: name the phase.
        // (enabledOnSeries can't keep the tooltip off the overlays: with an
        // unshared tooltip it hides it everywhere.)
        title: { formatter: (seriesName) => `${String(seriesName).replace(OVERLAY_SUFFIX, "")}: ` },
      },
    },
  };
};
